import React from 'react';

import { useSelector } from 'react-redux';
import {
  Trans,
  useTranslation,
  useExperiments,
  useEnvironment,
  useBi,
} from '@wix/yoshi-flow-editor';
import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import {
  groupsHeaderButtonClick,
  groupsListCtaButtonClick,
} from '@wix/bi-logger-groups-data/v2';

import {
  selectCanCreateEvents,
  selectEvents,
  selectGroupIdBySlug,
  selectIsAppAvailable,
  selectUpcomingAndPastEvents,
} from 'store/selectors';
import type { IRootState } from 'store/types';
import { GroupAppKey } from 'store/groups/types';

import { useDidUpdate } from 'common/hooks';
import { ErrorState } from 'wui/ErrorState';
import { EmptyState } from 'wui/EmptyState';
import { Hide } from 'wui/Hide';
import { Spinner } from 'wui/Spinner';
import { Button } from 'wui/Button';
import { Show } from 'wui/Show';
import { Box } from 'wui/Box';
import { Divider } from 'wui/Divider';

import { useController } from 'common/context/controller';
import { Event } from 'common/components/Event';

import { RestrictedState } from '../../RestrictedState';
import { Column, Layout } from '../../Layout';
import { EventListHeader } from './EventListHeader';
import { CreateEventButton } from './CreateEventButton';

export function EventListPage() {
  const { t } = useTranslation();
  const { params } = useCurrentStateAndParams();
  const { experiments } = useExperiments();
  const { isMobile } = useEnvironment();
  const bi = useBi();

  const { events$ } = useController();
  const groupId = useSelector(selectGroupIdBySlug(params.slug));
  let canCreateEvents = useSelector(selectCanCreateEvents(groupId));
  canCreateEvents =
    canCreateEvents && experiments.enabled('specs.groups.events-by-uou');

  const loading = useSelector(
    (state: IRootState) => state.events.fetch.loading,
  );
  const error = useSelector((state: IRootState) => state.events.fetch.error);
  const events = useSelector(selectEvents);
  const [upcoming, past] = useSelector(selectUpcomingAndPastEvents);

  const isAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.EVENTS_APP,
    }),
  );

  useDidUpdate(() => {
    if (isAvailable) {
      events$.fetch(groupId);
    }
  }, [isAvailable]);

  if (!isAvailable) {
    return <RestrictedState application={GroupAppKey.EVENTS_APP} />;
  }

  if (loading) {
    return <EmptyState wired variant="section" title={<Spinner />} />;
  }

  if (error) {
    return (
      <ErrorState
        data-hook="error-state"
        wired
        title={t('groups-web.discussion.error-state.title')}
        subtitle={
          <Trans
            i18nKey={t('groups-web.discussion.error-state.owner.message')}
            components={[<span key={0} />, <br key={1} />, <span key={2} />]}
          />
        }
        actionLabel={t('groups-web.discussion.error-state.cta')}
        onRetry={handleRetry}
      />
    );
  }

  if (!events.length) {
    return (
      <EmptyState
        data-hook="empty-state"
        wired
        title={t('groups-web.events.empty.title')}
        subtitle={t('groups-web.events.empty.subtitle')}
        action={
          <div>
            <CreateEventButton
              onClick={reportCreateEventClick}
              groupId={groupId}
            />
          </div>
        }
      />
    );
  }

  return (
    <>
      <Layout data-hook="event-list-page">
        <Column>
          <Show if={isMobile}>
            <Box padding="0 SP5">
              <CreateEventButton
                groupId={groupId}
                fullWidth
                onClick={reportHeaderCreateEventClick}
              />
            </Box>
            <Divider />
          </Show>

          <Hide if={!upcoming.length}>
            <div data-hook="upcoming-events">
              <EventListHeader
                groupId={groupId}
                label={t('groups-web.events.upcoming')}
                withCreateButton={!isMobile}
              />
              {upcoming.map((event, i) => (
                <Event key={event.id} event={event} groupId={groupId} />
              ))}
            </div>
          </Hide>

          <Hide if={!past.length}>
            <div data-hook="past-events">
              <EventListHeader
                groupId={groupId}
                label={t('groups-web.events.ended')}
                withCreateButton={!isMobile && !upcoming.length}
              />
              {past.map((event, i) => (
                <Event key={event.id} event={event} groupId={groupId} />
              ))}
            </div>
          </Hide>
        </Column>
      </Layout>
    </>
  );

  function handleRetry() {
    events$.fetch(groupId);
  }

  function reportCreateEventClick() {
    bi.report(
      groupsListCtaButtonClick({
        page_name: 'events_livesite',
        group_id: groupId,
        button_name: 'create_event',
      }),
    );
  }

  function reportHeaderCreateEventClick() {
    bi.report(
      groupsHeaderButtonClick({
        button_name: 'create_event',
        page_name: 'events_livesite',
        group_id: groupId,
      }),
    );
  }
}

EventListPage.displayName = 'EventListPage';
